import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Button, Tag, Spin, Badge, Dropdown, message } from 'antd';
import {
  MessageOutlined,
  ExpandOutlined,
  CloseOutlined,
  SendOutlined,
  PaperClipOutlined,
  RobotOutlined,
  UserOutlined,
  CompressOutlined,
  StopOutlined,
  BulbOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useChatCheckStore } from '../../stores/chatCheckStore';
import { useAuthStore } from '../../stores/authStore';
import type { ChatConversation, ChatMsg } from '../../stores/chatCheckStore';
import MarkdownMessage from './MarkdownMessage';
import { getStoredToken } from '../../utils/authToken';

const { TextArea } = Input;

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 500;
const TITLE_BAR_HEIGHT = 36;
const BUBBLE_SIZE = 48;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 360;

// ─── Helpers ────────────────────────────────────────────────────────────────

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

function formatThreadTime(timestamp?: number): string {
  if (!timestamp) return '';
  const delta = Date.now() - timestamp;
  const minutes = Math.max(1, Math.floor(delta / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function threadLabel(conversation: ChatConversation): string {
  const title = (conversation.title ?? '').trim();
  if (title) return title;
  const summary = (conversation.summary ?? '').replace(/^Compressed history from this conversation thread:\s*/i, '').trim();
  if (summary) return summary.split('\n')[0].replace(/^-\s*(User|Assistant):\s*/i, '');
  return 'Untitled Chat';
}

function threadSummary(conversation: ChatConversation): string {
  const summary = (conversation.summary ?? '').replace(/^Compressed history from this conversation thread:\s*/i, '').trim();
  if (!summary) return 'No summary yet';
  return summary.split('\n')[0].replace(/^-\s*(User|Assistant):\s*/i, '');
}

function ratingPathPlantId(path: string): string | null {
  const match = path.match(/^\/rating\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// ─── Welcome message ────────────────────────────────────────────────────────

const WELCOME: ChatMsg = {
  id: 'welcome',
  role: 'assistant',
  content: 'How can I help you today?',
  timestamp: Date.now(),
};

// ─── File checklist message (shown on first open of create-plant flow) ──────

const FILE_CHECKLIST_EN = `Hi! I'll guide you through setting up your plant for rating. Let's go step by step.

**Step 1 — Basic info**
Please tell me:
- Plant name (if you'd like to rename it)
- Location: city/country and GPS coordinates if available
- Installed capacity (kWp or MWp)
- Grid connection date
- Plant type (rooftop, ground-mount, floating, agri-PV, etc.)

You can type the answers, paste a summary, or upload a document — I'll extract what I need.

Recommended files you can upload now or later:
- Feasibility study report or investment memo
- Design proposal, layout drawings, single-line diagram, or as-built drawings
- Grid connection notice, interconnection approval, or commissioning documents
- Electricity bills, tariff/PPA contract, settlement statement, or revenue records
- Completion acceptance report, handover package, inspection report, or O&M records
- Equipment list, module/inverter datasheets, warranties, photos, CSV/Excel generation data, or SCADA exports

If you only have a few of these, upload what you have first. I can create a basic TAR from partial data and show what is still missing.

---
*Need to see the full data checklist? [View here →](/portfolio?checklist=1)*`;

const FILE_CHECKLIST_ZH = `你好！我来引导你一步步完成电站建档和评级。

**第一步 — 基础信息**
请告诉我：
- 电站名称（如需修改）
- 位置：城市/国家，以及经纬度坐标（如有）
- 装机容量（kWp 或 MWp）
- 并网时间
- 电站类型（屋顶、地面、漂浮、农光互补等）

你可以直接输入、粘贴摘要，或上传文件，我会自动提取所需信息。

建议现在或之后上传的资料包括：
- 可研报告、投资测算或项目说明
- 设计方案、总平面布置图、一次接线图、竣工图
- 并网通知、接入批复、调试/并网证明
- 电费单据、电价/PPA 合同、结算单或收入记录
- 竣工验收报告、移交资料、检测报告、运维记录
- 设备清单、组件/逆变器参数表、质保文件、现场照片、发电量 CSV/Excel、SCADA 导出数据

如果资料不全，也可以先上传已有文件。我会先基于部分数据生成基础 TAR，并提示还缺哪些关键资料。

---
*需要查看完整资料清单？[点击这里 →](/portfolio?checklist=1)*`;

function getFileChecklistMsg(lang: string): ChatMsg {
  return {
    id: 'file-checklist',
    role: 'assistant',
    content: lang.startsWith('zh') ? FILE_CHECKLIST_ZH : FILE_CHECKLIST_EN,
    timestamp: Date.now(),
  };
}

// ─── Message Bubble ─────────────────────────────────────────────────────────

const MsgBubble: React.FC<{ msg: ChatMsg }> = ({ msg }) => {
  const { t } = useTranslation();
  const isUser = msg.role === 'user';
  const [thinkingOpen, setThinkingOpen] = useState(false);

  // Thinking block (admin/operator ShowThinking mode)
  if (msg.role === 'action' && msg.actionType === 'thinking') {
    return (
      <div style={{ margin: '4px 0' }}>
        <div
          onClick={() => setThinkingOpen((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, color: 'rgba(255,255,255,0.4)',
            background: 'rgba(255,165,0,0.08)', border: '1px solid rgba(255,165,0,0.2)',
            borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
          }}
        >
          <BulbOutlined style={{ marginRight: 4 }} /> {thinkingOpen ? '▲' : '▼'} {t('Thinking...')}
        </div>
        {thinkingOpen && (
          <div style={{
            marginTop: 4, padding: '8px 10px',
            background: 'rgba(255,165,0,0.05)', border: '1px solid rgba(255,165,0,0.15)',
            borderRadius: 6, fontSize: 11, color: 'rgba(255,255,255,0.5)',
            fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto',
          }}>
            {msg.content}
          </div>
        )}
      </div>
    );
  }

  // Action messages: centered label
  if (msg.role === 'action') {
    return (
      <div style={{ textAlign: 'center', margin: '6px 0' }}>
        <span style={{
          display: 'inline-block',
          fontSize: 11,
          color: 'rgba(255,255,255,0.35)',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
          padding: '2px 10px',
          fontStyle: 'italic',
        }}>
          {msg.content}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        marginBottom: 12,
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: isUser ? '#1677ff' : 'rgba(255,255,255,0.10)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isUser ? (
          <UserOutlined style={{ color: '#fff', fontSize: 12 }} />
        ) : (
          <RobotOutlined style={{ color: '#1677ff', fontSize: 12 }} />
        )}
      </div>
      <div
        style={{
          maxWidth: '78%',
          background: isUser ? '#1677ff' : 'rgba(24,144,255,0.08)',
          color: isUser ? '#fff' : 'rgba(255,255,255,0.88)',
          borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
          padding: '8px 12px',
          fontSize: 13,
          lineHeight: 1.6,
          wordBreak: 'break-word',
        }}
      >
        {msg.content ? (
          isUser ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
          ) : (
            <MarkdownMessage content={msg.content} />
          )
        ) : (
          <Spin size="small" style={{ margin: '2px 8px' }} />
        )}
        {msg.files && msg.files.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {msg.files.map((f, i) => (
              <Tag key={i} style={{ fontSize: 11 }}>
                <PaperClipOutlined /> {f}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── UnifiedChat Component ──────────────────────────────────────────────────

interface UnifiedChatProps {
  /** When true, renders inline (no floating) for embedded use in ChatAnalysis */
  embedded?: boolean;
  /** Plant ID context — auto-passed with messages when embedded */
  plantId?: string;
  /** When true, suppresses the floating bubble (used when FloatingActionBar provides the toggle) */
  hideBubble?: boolean;
}

const UnifiedChat: React.FC<UnifiedChatProps> = ({ embedded = false, plantId, hideBubble = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const {
    messages,
    isOpen,
    isMinimized,
    isStreaming,
    position,
    sendMessage,
    setPosition,
    setOpen,
    setMinimized,
    abortStreaming,
    selectedModel,
    availableModels,
    setSelectedModel,
    loadAvailableModels,
    conversations,
    loadConversations,
    switchConversation,
    ensureConversation,
    startNewConversation,
    activeConversationId,
    pageContext,
    setShowThinking,
  } = useChatCheckStore();

  const effectivePlantId = plantId ?? pageContext?.plantId;

  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [size, setSize] = useState({ w: DEFAULT_WIDTH, h: DEFAULT_HEIGHT });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobile, setMobile] = useState(isMobile());
  const [threadMenuOpen, setThreadMenuOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threadMenuRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const resizeRef = useRef<{
    startX: number;
    startY: number;
    origW: number;
    origH: number;
  } | null>(null);

  // ─── Responsive ───────────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Default position ─────────────────────────────────────────────

  useEffect(() => {
    if (!embedded && position.x === -1) {
      setPosition(
        window.innerWidth - DEFAULT_WIDTH - 24,
        window.innerHeight - DEFAULT_HEIGHT - 24,
      );
    }
  }, [embedded, position.x, setPosition]);

  // ─── Auto-scroll ──────────────────────────────────────────────────

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!threadMenuOpen) return;
    const handleOutside = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (threadMenuRef.current && target && !threadMenuRef.current.contains(target)) {
        setThreadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [threadMenuOpen]);

  useEffect(() => {
    const handleSeedDraft = (event: Event) => {
      const customEvent = event as CustomEvent<{ plantId?: string; text?: string }>;
      const nextText = customEvent.detail?.text?.trim();
      const targetPlantId = customEvent.detail?.plantId;
      if (!nextText) return;
      if (targetPlantId && effectivePlantId && targetPlantId !== effectivePlantId) return;
      setInput((current) => (current.trim() ? current : nextText));
    };

    window.addEventListener('rater:chat-seed-draft', handleSeedDraft as EventListener);
    return () => window.removeEventListener('rater:chat-seed-draft', handleSeedDraft as EventListener);
  }, [effectivePlantId]);

  // ─── Execute pending actions from SSE ─────────────────────────────

  const pendingActions = useChatCheckStore((s) => s.pendingActions);
  const clearActions = useChatCheckStore((s) => s.clearActions);

  useEffect(() => {
    if (pendingActions.length === 0) return;

    for (const act of pendingActions) {
      switch (act.action) {
        case 'navigate':
          if (act.target) {
            const path = act.target.replace(/^\/rater/, '') || '/';
            const ratingPlantId = ratingPathPlantId(path);
            if (
              ratingPlantId &&
              (ratingPlantId === effectivePlantId || ratingPlantId === pageContext?.plantId) &&
              !pageContext?.plantRating &&
              pageContext?.plantStatus !== 'rated'
            ) {
              if (window.location.pathname !== `/chat/${ratingPlantId}`) {
                navigate(`/chat/${ratingPlantId}`);
              }
              break;
            }
            navigate(path);
          }
          break;
        case 'refresh_plants':
          // Trigger plant list reload via checkStore if available
          break;
        case 'download_pdf':
          if (act.plant_id) {
            const token = getStoredToken();
            fetch(`/api/v2/plants/${act.plant_id}/report/pdf`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            })
              .then((r) => r.blob())
              .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'report.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              });
          }
          break;
        case 'show_toast':
          if (act.level === 'success') message.success(act.message || t('Done'));
          else if (act.level === 'warning') message.warning(act.message || t('Done'));
          else if (act.level === 'error') message.error(act.message || t('Done'));
          else message.info(act.message || t('Done'));
          break;
        case 'refresh_support_requests':
          window.dispatchEvent(new CustomEvent('rater:support-requests-updated', {
            detail: { plantId: act.plant_id },
          }));
          break;
        case 'refresh_rating':
          window.dispatchEvent(new CustomEvent('rater:rating-updated', {
            detail: { plantId: act.plant_id },
          }));
          break;
        case 'plant_updated':
          window.dispatchEvent(new CustomEvent('rater:plant-updated', {
            detail: { plantId: act.plant_id },
          }));
          message.success(t('Plant data updated'));
          break;
      }
    }
    clearActions();
  }, [pendingActions, clearActions, navigate, t]);

  // Floating chat stays visible on all pages (no auto-hide).
  const shouldHide = false;

  // ─── Drag handlers (title bar) ────────────────────────────────────

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (mobile || embedded) return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };

      const handleMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        setPosition(
          Math.max(0, Math.min(window.innerWidth - 200, dragRef.current.origX + dx)),
          Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.origY + dy)),
        );
      };
      const handleUp = () => {
        dragRef.current = null;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    },
    [mobile, embedded, position, setPosition],
  );

  // ─── Resize handler (bottom-right corner) ────────────────────────

  const handleResizeDown = useCallback(
    (e: React.MouseEvent) => {
      if (mobile || embedded) return;
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origW: size.w,
        origH: size.h,
      };

      const handleMove = (ev: MouseEvent) => {
        if (!resizeRef.current) return;
        const dx = ev.clientX - resizeRef.current.startX;
        const dy = ev.clientY - resizeRef.current.startY;
        setSize({
          w: Math.max(MIN_WIDTH, resizeRef.current.origW + dx),
          h: Math.max(MIN_HEIGHT, resizeRef.current.origH + dy),
        });
      };
      const handleUp = () => {
        resizeRef.current = null;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    },
    [mobile, embedded, size],
  );

  // ─── Send ─────────────────────────────────────────────────────────

  const handleSend = useCallback(() => {
    const hasContent = input.trim() || selectedFiles.length > 0;
    if (!hasContent || isStreaming) return;
    sendMessage(input, plantId, selectedFiles.length > 0 ? selectedFiles : undefined);
    setInput('');
    setSelectedFiles([]);
  }, [input, isStreaming, plantId, sendMessage, selectedFiles]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // ─── File picker ──────────────────────────────────────────────────

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        setSelectedFiles(Array.from(e.target.files));
      }
    },
    [],
  );

  // ─── Display messages ─────────────────────────────────────────────

  const isCreateFlow = embedded && pageContext?.type === 'plant_detail';
  const displayMessages = messages.length > 0
    ? messages
    : isCreateFlow
    ? [getFileChecklistMsg(i18n.language)]
    : [{ ...WELCOME, content: t('How can I help you today?') }];

  const role = user?.role ?? 'person';

  // Load models on mount and whenever chat opens
  useEffect(() => {
    if (user) {
      loadAvailableModels();
    }
  }, [user, isOpen, loadAvailableModels]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShowThinking(role === 'admin' || role === 'operator');
  }, [role, setShowThinking]);

  // When hideBubble is active, never allow minimized bubble state — force close instead
  useEffect(() => {
    if (hideBubble && isMinimized) {
      setMinimized(false);
      setOpen(false);
    }
  }, [hideBubble, isMinimized, setMinimized, setOpen]);

  // Only resolve/create a conversation when chat is visible or embedded.
  useEffect(() => {
    if (!user) return;
    if (!embedded && !isOpen) return;
    ensureConversation(effectivePlantId)
      .then(() => loadConversations(effectivePlantId))
      .catch(() => {});
  }, [user, effectivePlantId, embedded, isOpen, ensureConversation, loadConversations]);

  const handleNewChat = useCallback(() => {
    if (isStreaming) return;
    startNewConversation(effectivePlantId).catch(() => {
      message.error(t('Failed to start a new chat'));
    });
  }, [effectivePlantId, isStreaming, startNewConversation, t]);

  const handleThreadMenuClick = useCallback(async (key: string) => {
    setThreadMenuOpen(false);
    if (key === 'new') {
      handleNewChat();
      return;
    }
    try {
      await switchConversation(key, effectivePlantId);
    } catch {
      message.error(t('Failed to switch chat thread'));
    }
  }, [effectivePlantId, handleNewChat, switchConversation, t]);

  // ─── Hidden state: just a floating bubble ─────────────────────────

  if (!embedded && !isOpen) {
    if (hideBubble) return null;
    return (
      <Button
        type="primary"
        shape="circle"
        icon={
          <Badge count={messages.filter((m) => m.role === 'assistant').length > 0 ? 0 : undefined} dot>
            <MessageOutlined style={{ color: '#fff', fontSize: 20 }} />
          </Badge>
        }
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 2000,
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => setOpen(true)}
      />
    );
  }

  // ─── Full-screen overlay on mobile ────────────────────────────────

  const containerStyle: React.CSSProperties =
    embedded
        ? {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#141414',
            borderRadius: 8,
          overflow: 'hidden',
        }
      : mobile
        ? {
            position: 'fixed',
            right: 16,
            bottom: 16,
            width: isMinimized ? BUBBLE_SIZE : Math.min(window.innerWidth - 32, 380),
            height: isMinimized ? BUBBLE_SIZE : Math.min(window.innerHeight * 0.65, 520),
            zIndex: 2000,
            background: isMinimized ? 'rgba(22,119,255,0.9)' : 'rgba(20,20,20,0.88)',
            backdropFilter: 'blur(16px)',
            borderRadius: isMinimized ? '50%' : 14,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            border: isMinimized ? 'none' : '1px solid rgba(255,255,255,0.12)',
            transition: 'width 0.25s ease, height 0.25s ease, border-radius 0.25s ease',
          }
        : {
            position: 'fixed',
            left: isFullscreen ? 16 : position.x,
            top: isFullscreen ? 16 : position.y,
            width: size.w,
            height: size.h,
            zIndex: 2000,
            background: '#141414',
            borderRadius: isFullscreen ? 6 : 10,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 6px 24px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)',
            border: '1px solid rgba(255,255,255,0.10)',
            transition: 'width 0.25s ease, height 0.25s ease, left 0.25s ease, top 0.25s ease',
          };

  // Early return AFTER all hooks
  if (shouldHide) return null;

  return (
    <div style={containerStyle}>
      {/* ── Title bar ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 10px',
          height: TITLE_BAR_HEIGHT,
          background: 'rgba(255,255,255,0.04)',
          borderBottom: isMinimized ? 'none' : '1px solid rgba(255,255,255,0.10)',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <span
          onMouseDown={handleMouseDown}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, cursor: embedded ? 'default' : mobile ? 'default' : 'grab', flex: 1 }}
        >
          <span style={{ fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.88)' }}>
            {t('Rater AI')}
          </span>
        </span>

        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <div ref={threadMenuRef} style={{ position: 'relative' }}>
            <Button
              type="text"
              size="small"
              disabled={isStreaming}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (isStreaming) return;
                setThreadMenuOpen((open) => !open);
              }}
              style={{ height: 26, padding: '0 8px', color: 'rgba(255,255,255,0.78)', fontSize: 11 }}
            >
              {t('Threads')} <DownOutlined style={{ fontSize: 10 }} />
            </Button>
            {threadMenuOpen && (
              <div
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: 30,
                  right: 0,
                  width: 272,
                  maxHeight: 320,
                  overflowY: 'auto',
                  background: 'rgba(15,18,24,0.98)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10,
                  boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                  zIndex: 20,
                  padding: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => handleThreadMenuClick('new')}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'rgba(22,119,255,0.16)',
                    color: 'rgba(255,255,255,0.96)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginBottom: 6,
                  }}
                >
                  {t('New Chat')}
                </button>
                {conversations.length === 0 ? (
                  <div style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
                    {t('No previous conversations yet')}
                  </div>
                ) : (
                  conversations.map((conversation) => {
                    const active = activeConversationId === conversation.id;
                    return (
                      <button
                        key={conversation.id}
                        type="button"
                        onClick={() => handleThreadMenuClick(conversation.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: active ? 'rgba(22,119,255,0.18)' : 'transparent',
                          color: 'inherit',
                          borderRadius: 8,
                          padding: '9px 10px',
                          cursor: 'pointer',
                          display: 'block',
                          marginBottom: 2,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                          <span style={{ color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: active ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {threadLabel(conversation) === 'Untitled Chat' ? t('Untitled Chat') : threadLabel(conversation)}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, flexShrink: 0 }}>
                            {formatThreadTime(conversation.updated_at)}
                          </span>
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {threadSummary(conversation) === 'No summary yet' ? t('No summary yet') : threadSummary(conversation)}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          {availableModels.length > 0 && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayClassName="chat-model-dropdown"
              overlayStyle={{ zIndex: 5000 }}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              menu={{
                items: availableModels.map((m) => ({
                  key: m.id,
                  label: (
                    <div style={{ minWidth: 160 }}>
                      <div style={{ fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: 11, opacity: 0.72 }}>{m.description}</div>
                    </div>
                  ),
                  onClick: () => setSelectedModel(m.id),
                })),
              }}
            >
              <Button
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                icon={<DownOutlined />}
                style={{ minWidth: 96, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                {availableModels.find((m) => m.id === selectedModel)?.name || selectedModel || t('Model')}
              </Button>
            </Dropdown>
          )}
          {!embedded && (
          <span style={{ display: 'flex', gap: 2 }}>
            <Button
              type="text"
              size="small"
              icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (isFullscreen) {
                  setSize({ w: DEFAULT_WIDTH, h: DEFAULT_HEIGHT });
                  setIsFullscreen(false);
                } else {
                  setSize({ w: window.innerWidth - 32, h: window.innerHeight - 32 });
                  setIsFullscreen(true);
                }
              }}
              style={{ width: 26, height: 26, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}
              title={isFullscreen ? t('Restore') : t('Fullscreen')}
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                setMinimized(false);
                setIsFullscreen(false);
              }}
              style={{ width: 26, height: 26, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}
              title={t('Close')}
            />
          </span>
        )}
        </span>
      </div>

      {/* ── Content (hidden when minimized) ───────────────────── */}
      {!isMinimized && (
        <>
          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '12px 12px 4px',
              minHeight: 0,
            }}
          >
            {displayMessages.map((msg) => (
              <MsgBubble key={msg.id} msg={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div
              style={{
                padding: '4px 12px',
                borderTop: '1px solid rgba(255,255,255,0.10)',
                display: 'flex',
                gap: 4,
                flexWrap: 'wrap',
              }}
            >
              {selectedFiles.map((f, i) => (
                <Tag
                  key={i}
                  closable
                  onClose={() =>
                    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))
                  }
                  style={{ fontSize: 11 }}
                >
                  <PaperClipOutlined /> {f.name}
                </Tag>
              ))}
            </div>
          )}

          {/* Input area */}
          <div
            style={{
              padding: '8px 10px 12px',
              borderTop: '1px solid rgba(255,255,255,0.10)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              style={{ width: 40, height: 40, color: 'rgba(255,255,255,0.55)', flexShrink: 0, fontSize: 18 }}
              title={t('Attach file')}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('Type a message...')}
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={isStreaming}
              style={{
                borderRadius: 8,
                resize: 'none',
                fontSize: 13,
              }}
            />
            {isStreaming ? (
              <Button
                type="default"
                danger
                icon={<StopOutlined />}
                onClick={abortStreaming}
                style={{ borderRadius: 8, height: 40, width: 40, flexShrink: 0, fontSize: 18 }}
                title={t('Stop')}
              />
            ) : (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                disabled={!input.trim() && selectedFiles.length === 0}
                style={{ borderRadius: 8, height: 40, width: 40, flexShrink: 0, fontSize: 18 }}
                title={t('Send')}
              />
            )}
          </div>

          {/* Resize handle (bottom-right) — desktop floating only */}
          {!embedded && !mobile && (
            <div
              onMouseDown={handleResizeDown}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: 14,
                height: 14,
                cursor: 'se-resize',
                background:
                  'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 60%, transparent 60%, transparent 70%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,0.15) 80%, transparent 80%)',
                borderRadius: '0 0 10px 0',
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default UnifiedChat;
